import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, User, Phone, Mail, Calendar, Building, Linkedin, Briefcase } from 'lucide-react';

const EditExternalContactModal = ({ contact, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    title: '',
    department: '',
    company: '',
    phone_numbers: [],
    date_of_birth: '',
    contact_type: 'client',
    primary_contact_id: '',
    profile_photo_url: '',
    linkedin_profile: '',
    internal_comments: '',
    confidential_notes: '',
    // Executive Assistant fields
    assistant_name: '',
    assistant_email: '',
    assistant_phone: ''
  });

  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadExecutives();
    populateFormData();
  }, [contact]);

  const loadExecutives = async () => {
    try {
      const response = await axios.get('/executives');
      setExecutives(response.data);
    } catch (err) {
      console.error('Failed to load executives:', err);
    }
  };

  const populateFormData = () => {
    if (contact) {
      setFormData({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        email: contact.email || '',
        title: contact.title || '',
        department: contact.department || '',
        company: contact.company || '',
        phone_numbers: contact.phone_numbers || [],
        date_of_birth: contact.date_of_birth || '',
        contact_type: contact.contact_type || 'client',
        primary_contact_id: contact.primary_contact_id || '',
        profile_photo_url: contact.profile_photo_url || '',
        linkedin_profile: contact.linkedin_profile || '',
        internal_comments: contact.internal_comments || '',
        confidential_notes: contact.confidential_notes || '',
        // Executive Assistant fields
        assistant_name: contact.assistant_name || '',
        assistant_email: contact.assistant_email || '',
        assistant_phone: contact.assistant_phone || ''
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhoneChange = (index, field, value) => {
    const newPhones = [...formData.phone_numbers];
    newPhones[index] = { ...newPhones[index], [field]: value };
    setFormData(prev => ({
      ...prev,
      phone_numbers: newPhones
    }));
  };

  const addPhoneNumber = () => {
    setFormData(prev => ({
      ...prev,
      phone_numbers: [
        ...prev.phone_numbers,
        { type: 'office', number: '', is_primary: prev.phone_numbers.length === 0 }
      ]
    }));
  };

  const removePhoneNumber = (index) => {
    const newPhones = formData.phone_numbers.filter((_, i) => i !== index);
    // If we removed the primary phone, make the first one primary
    if (newPhones.length > 0 && !newPhones.some(p => p.is_primary)) {
      newPhones[0].is_primary = true;
    }
    setFormData(prev => ({
      ...prev,
      phone_numbers: newPhones
    }));
  };

  const setPrimaryPhone = (index) => {
    const newPhones = formData.phone_numbers.map((phone, i) => ({
      ...phone,
      is_primary: i === index
    }));
    setFormData(prev => ({
      ...prev,
      phone_numbers: newPhones
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.first_name.trim() || !formData.last_name.trim()) {
        throw new Error('First name and last name are required');
      }

      // Convert date_of_birth to proper format if provided
      const submitData = {
        ...formData,
        date_of_birth: formData.date_of_birth || null
      };

      const response = await axios.put(`/external-contacts/${contact.id}`, submitData);
      onSave(response.data);
    } catch (err) {
      setError('Failed to update contact: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const contactTypes = [
    { value: 'client', label: 'Client' },
    { value: 'prospect', label: 'Prospect' },
    { value: 'business_partner', label: 'Business Partner' },
    { value: 'affiliate', label: 'Affiliate' },
    { value: 'private', label: 'Private' }
  ];

  const phoneTypes = [
    { value: 'office', label: 'Office' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'home', label: 'Home' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[70]">
      <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-900">Edit External Contact</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Basic Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Professional Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Building className="w-5 h-5" />
              Professional Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., CEO, Director, Manager"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department
                </label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => handleInputChange('department', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Sales, Marketing, IT"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => handleInputChange('company', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Phone className="w-5 h-5" />
              Contact Information
            </h4>
            
            {/* Phone Numbers */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Phone Numbers
                </label>
                <button
                  type="button"
                  onClick={addPhoneNumber}
                  className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                >
                  <Plus className="w-4 h-4" />
                  Add Phone
                </button>
              </div>
              
              {formData.phone_numbers.map((phone, index) => (
                <div key={index} className="flex items-center gap-2 mb-2">
                  <select
                    value={phone.type}
                    onChange={(e) => handlePhoneChange(index, 'type', e.target.value)}
                    className="border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    {phoneTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    value={phone.number}
                    onChange={(e) => handlePhoneChange(index, 'number', e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Phone number"
                  />
                  <label className="flex items-center text-sm">
                    <input
                      type="radio"
                      name="primary_phone"
                      checked={phone.is_primary}
                      onChange={() => setPrimaryPhone(index)}
                      className="mr-1"
                    />
                    Primary
                  </label>
                  <button
                    type="button"
                    onClick={() => removePhoneNumber(index)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* LinkedIn Profile */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                LinkedIn Profile
              </label>
              <input
                type="url"
                value={formData.linkedin_profile}
                onChange={(e) => handleInputChange('linkedin_profile', e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://linkedin.com/in/username"
              />
            </div>
          </div>

          {/* Executive Assistant Information */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Briefcase className="w-5 h-5" />
              Executive Assistant
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assistant Name
                </label>
                <input
                  type="text"
                  value={formData.assistant_name}
                  onChange={(e) => handleInputChange('assistant_name', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assistant Phone
                </label>
                <input
                  type="tel"
                  value={formData.assistant_phone}
                  onChange={(e) => handleInputChange('assistant_phone', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assistant Email
                </label>
                <input
                  type="email"
                  value={formData.assistant_email}
                  onChange={(e) => handleInputChange('assistant_email', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Email address"
                />
              </div>
            </div>
          </div>

          {/* Contact Type and Relationship */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              Contact Type & Relationship
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.contact_type}
                  onChange={(e) => handleInputChange('contact_type', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {contactTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Internal Contact
                </label>
                <select
                  value={formData.primary_contact_id}
                  onChange={(e) => handleInputChange('primary_contact_id', e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select an executive...</option>
                  {executives.map(exec => (
                    <option key={exec.id} value={exec.id}>
                      {exec.first_name} {exec.last_name} - {exec.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-lg font-semibold text-gray-900 mb-4">
              Notes & Comments
            </h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Internal Comments
                </label>
                <textarea
                  value={formData.internal_comments}
                  onChange={(e) => handleInputChange('internal_comments', e.target.value)}
                  rows={3}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Internal notes visible to organization members..."
                />
              </div>
              {/* Only show confidential notes if user can view them */}
              {contact.can_view_confidential && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confidential Notes
                  </label>
                  <textarea
                    value={formData.confidential_notes}
                    onChange={(e) => handleInputChange('confidential_notes', e.target.value)}
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Confidential notes visible only to primary contact and creator..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Only visible to the primary internal contact and the person who created this contact.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Contact'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditExternalContactModal;